import React, { useState, useEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Box, Card, CardContent, Typography, Button, Divider, List, ListItem, ListItemText, ListItemAvatar, Avatar, Checkbox, FormControlLabel } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import UploadButton from './UploadButton';
import { auth } from '../../firebase';
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import ClockIcon from '@mui/icons-material/AccessTime';
import ImageConfig from './ImageConfig';
import ArrangeButton from './ArrangeButton';
import { v4 as uuidv4 } from 'uuid';

import RecentlyUploaded from './RecentlyUploaded';
import { roundSize } from '../../utils/money';
import { DPI } from '../../config/dtfConfig';

export default function ImageControlPanel({ images, setImages, onProceedClick }) {

  const [sessionId, setSessionId] = useState(null);

  const CANVAS_WIDTH = 22; // in inches
  const CANVAS_HEIGHT = 72; // assumed value, adjust as needed

  const [localImages, setLocalImages] = useState(images);


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let sessionId = params.get('sessionId');
    if (!sessionId) {
      // assign a new sessionId and update URL
      sessionId = uuidv4();
      params.set('sessionId', sessionId);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
    setSessionId(sessionId);

    const openReq = indexedDB.open('dtfSessionDb');
    openReq.onupgradeneeded = () => {
      const db = openReq.result;
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions');
      }
      if (!db.objectStoreNames.contains('checkout')) {
        db.createObjectStore('checkout');
      }
    };
    openReq.onerror = () => {
      console.error('Failed to open IndexedDB:', openReq.error);
    };
    openReq.onsuccess = () => {
      // prepare serializable images array
      const serializableImages = images.map(img => ({
        src: img.url || img.src,
        name: img.name,
        widthIn: img.widthIn,
        heightIn: img.heightIn,
        quantity: img.quantity,
        scale: img.scale,
        lockAspectRatio: img.lockAspectRatio,
        fromRecent: img.fromRecent === true,
      }));
      const db = openReq.result;
      const tx = db.transaction('sessions', 'readwrite');
      const store = tx.objectStore('sessions');
      const getReq = store.get(sessionId);
      getReq.onsuccess = () => {
        const record = getReq.result;
        if (record && Array.isArray(record.images)) {
          console.log('Loaded session images from IndexedDB:', record.images[0].url);
          setLocalImages(record.images);
          setImages(record.images);
        } else {
          // no existing record – create one with current images
          store.put({ images: serializableImages }, sessionId);
          console.log(`Created new session entry ${sessionId} with images:`, images);
        }
      };
      getReq.onerror = () => {
        console.error(`Error getting session ${sessionId}:`, getReq.error);
        // still create record
        store.put({ images: serializableImages }, sessionId);
        console.log(`Created new session entry ${sessionId} after get error`);
      };
    };
  }, []);

  const saveSessionImages = (imgs) => {
    if (!sessionId) return;
    const serializableImages = imgs.map(img => ({
      src: img.url || img.src,
      name: img.name,
      widthIn: img.widthIn,
      heightIn: img.heightIn,
      quantity: img.quantity,
      scale: img.scale,
      lockAspectRatio: img.lockAspectRatio,
      fromRecent: img.fromRecent === true,
    }));
    const openReq2 = indexedDB.open('dtfSessionDb');
    openReq2.onsuccess = () => {
      const db2 = openReq2.result;
      const tx2 = db2.transaction('sessions', 'readwrite');
      const store2 = tx2.objectStore('sessions');
      store2.put({ images: serializableImages }, sessionId);
    };
    openReq2.onerror = () => {
      console.error('Failed to open IndexedDB for saving session:', openReq2.error);
    };
  };

  // Arrange trigger after images state update
  const [shouldProceed, setShouldProceed] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [libraryNames, setLibraryNames] = useState([]);

  // Drag and drop handlers
  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    const loadImagePromises = imageFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const widthIn = roundSize(img.width / DPI);
            const heightIn = roundSize(img.height / DPI);
            const newImg = {
              src: e.target.result,
              name: file.name,
              widthIn,
              heightIn,
              quantity: 1,
              scale: 100,
              lockAspectRatio: true,
              image: img
            };
            resolve(newImg);
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(loadImagePromises).then(newImages => {
      console.log('Uploaded images via drop:', newImages);
      setLocalImages(prev => {
        const updated = [...prev, ...newImages];
        saveSessionImages(updated);
        return updated;
      });
    });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Load existing library image names (user's pictures/* in Storage)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    let cancelled = false;
    const loadLibraryNames = async () => {
      try {
        const storage = getStorage();
        const baseRef = ref(storage, `pictures/${user.uid}`);
        const seen = new Set();
        const stack = [baseRef];
        while (stack.length) {
          const currentRef = stack.pop();
          const res = await listAll(currentRef);
          res.items.forEach((item) => {
            if (item.name) {
              seen.add(item.name.toLowerCase());
            }
          });
          res.prefixes.forEach((p) => stack.push(p));
        }
        if (!cancelled) {
          setLibraryNames(Array.from(seen));
        }
      } catch (err) {
        console.error('[ImageControlPanel] Failed to load library image names:', err);
      }
    };
    loadLibraryNames();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (shouldProceed) {
      onProceedClick();
      setShouldProceed(false);
    }
  }, [shouldProceed, onProceedClick]);


  const updateImage = (index, key, value) => {


    const newImages = [...localImages];
    const orig = localImages[index];
    const originalImg = orig.image || { width: orig.widthIn, height: orig.heightIn }; // loaded Image() object or fallback

    if (key === 'scale') {
      // scale branch stays the same
      const scale = value;
      newImages[index] = {
        ...orig,
        scale,
        widthIn: roundSize((originalImg?.width || 100) * scale / 100),
        heightIn: roundSize((originalImg?.height || 100) * scale / 100),
      };

    } else if (key === 'widthIn' && orig.lockAspectRatio) {
      if (value === '') {
        // user cleared the field: blank out both
        newImages[index] = { ...orig, widthIn: '', heightIn: '' };
      } else {
        // compute height from original image aspect ratio
        const newW = value; // already a number coming in
        const ratio = (originalImg.height || 1) / (originalImg.width || 1);
        newImages[index] = {
          ...orig,
          widthIn: roundSize(newW),
          heightIn: roundSize(newW * ratio),
        };
      }

    } else if (key === 'heightIn' && orig.lockAspectRatio) {
      if (value === '') {
        // user cleared the field: blank out both
        newImages[index] = { ...orig, widthIn: '', heightIn: '' };
      } else {
        // compute width from original image aspect ratio
        const newH = value;
        const ratio = (originalImg.width || 1) / (originalImg.height || 1);
        newImages[index] = {
          ...orig,
          heightIn: roundSize(newH),
          widthIn: roundSize(newH * ratio),
        };
      }

    } else if (key === 'name') {
      newImages[index] = { ...orig, name: value };

    } else {
      // everything else (quantity, lockAspectRatio toggle, etc.)
      newImages[index] = { ...orig, [key]: value };
    }

    const next = newImages[index];
    const current = localImages[index];
    const keys = new Set([...Object.keys(next), ...Object.keys(current)]);
    const hasChanged = Array.from(keys).some(key => next[key] !== current[key]);

    if (hasChanged) {
      setLocalImages(newImages);
      saveSessionImages(newImages);
    }
  };

  // Compute per-image warnings for visual outlines and gating proceed-to-checkout.
  const imageWarnings = useMemo(() => {
    const libraryNameSet = new Set(
      (libraryNames || []).map((n) => String(n || '').toLowerCase()).filter(Boolean)
    );
    const nameCounts = {};
    localImages.forEach((img) => {
      const rawName = typeof img?.name === 'string' ? img.name.trim() : '';
      if (!rawName) return;
      const key = rawName.toLowerCase();
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    });
    return localImages.map((img) => {
      const nameRaw = typeof img?.name === 'string' ? img.name.trim() : '';
      const nameKey = nameRaw.toLowerCase();
      const widthInNum = Number(img.widthIn);
      const heightInNum = Number(img.heightIn);
      const qtyNum = Number(img.quantity);

      const missingName = !nameRaw;
      const missingQuantity = !Number.isFinite(qtyNum) || qtyNum <= 0;
      const missingWidth =
        !Number.isFinite(widthInNum) || widthInNum <= 0;
      const missingHeight =
        !Number.isFinite(heightInNum) || heightInNum <= 0;
      const exceedsWidth =
        !missingWidth && widthInNum > CANVAS_WIDTH;
      const exceedsHeight =
        !missingHeight && heightInNum > CANVAS_HEIGHT;
      const missingSize = missingWidth || missingHeight;
      const exceedsCanvas = exceedsWidth || exceedsHeight;
      const duplicateName =
        !!nameRaw && nameCounts[nameKey] && nameCounts[nameKey] > 1;
      // Warn when a non-recent image's name matches an existing library image name
      const nameClashesLibrary =
        !!nameRaw && img?.fromRecent !== true && libraryNameSet.has(nameKey);
      const hasWarning =
        missingName ||
        missingQuantity ||
        missingWidth ||
        missingHeight ||
        exceedsWidth ||
        exceedsHeight ||
        duplicateName ||
        nameClashesLibrary;
      return {
        missingName,
        missingQuantity,
        missingWidth,
        missingHeight,
        missingSize,
        exceedsWidth,
        exceedsHeight,
        exceedsCanvas,
        duplicateName,
        nameClashesLibrary,
        hasWarning,
      };
    });
  }, [localImages, CANVAS_WIDTH, CANVAS_HEIGHT, libraryNames]);

  const hasAnyWarnings = imageWarnings.some((w) => w.hasWarning);
  const arrangerRef = useRef();
  return (
    <>
      {showError && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: 3000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Box sx={{
            position: 'relative',
            backgroundColor: '#fff3cd',
            color: '#856404',
            border: '2px solid #ffeeba',
            padding: '1rem 2rem',
            borderRadius: '8px',
            fontSize: '1.2rem',
            fontFamily: 'Anek Latin',
            fontWeight: 'bold',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            ⚠️ {errorMessage || 'Please fill out all fields before arranging.'}
            <Button
              onClick={() => setShowError(false)}
              variant="contained"
              sx={{
                mt: 2,
                backgroundColor: '#856404',
                color: '#fff3cd',
                '&:hover': {
                  backgroundColor: '#6c4d03'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      )}
      <Box onDrop={handleDrop} onDragOver={handleDragOver}>
        <Box sx={{ display: 'flex' }}>
          {/* Sidebar */}
          <Box sx={{ width: 240, p: 2, borderRight: '1px solid #ddd' }}>
            <div style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <h1 style={{ fontFamily: 'Anek Latin' }}>FILE MANAGER</h1>
            </div>
            <UploadButton onUpload={(newImages) => {
              console.log('Uploaded images:', newImages);
              setLocalImages(prev => {
                const updated = [...prev, ...newImages];
                saveSessionImages(updated);
                return updated;
              });
            }} />
            <Button variant="text" startIcon={<ClockIcon />} sx={{
              mb: 1,
              backgroundColor: '#94C940',
              color: 'white',
              borderRadius: '8px',
              px: 3,
              py: 0,
              fontWeight: '1000',
              fontFamily: 'Anek Latin',
              fontSize: '1.4rem',
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: '#94C940',
              }
            }} onClick={() => setShowRecent(true)}>Recent</Button>

            <List>
              {localImages.map((img, i) => (
                <ListItem key={i} sx={{ mb: 1 }}>
                  <ListItemAvatar>
                    <Avatar variant="square" src={img.src} sx={{ width: 40, height: 40 }} />
                  </ListItemAvatar>
                  <ListItemText primary={`Image ${i + 1}`} />
                  <IconButton edge="end" onClick={() => {
                    setLocalImages(prev => {
                      const updated = [...prev];
                      updated.splice(i, 1);
                      saveSessionImages(updated);
                      return updated;
                    });
                  }}>
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Controls */}
          <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto', maxHeight: 'calc(70vh - 32px)' }}>
            {localImages.map((img, i) => (
              <ImageConfig
                key={i}
                img={img}
                index={i}
                CANVAS_WIDTH={CANVAS_WIDTH}
                CANVAS_HEIGHT={CANVAS_HEIGHT}
                updateImage={updateImage}
                warning={imageWarnings[i]}
              />
            ))}
            <ArrangeButton disabled={hasAnyWarnings} onClick={() => {
              console.log('🧠 Arrange button clicked');

              // Always hide the error popup before doing anything
              setShowError(false);
              setErrorMessage('');

              if (hasAnyWarnings) {
                setErrorMessage('Please resolve the highlighted warnings (name, quantity, size, and canvas size) before proceeding to checkout.');
                setShowError(true);
                return;
              }

              if (window.__arrangeClickRunning) {
                console.log('⚠️ Arrange already running, skipping...');
                return;
              }
              window.__arrangeClickRunning = true;

              flushSync(() => {
                setImages(localImages);
              });

              setTimeout(() => {
                try {
              setShouldProceed(true);
            } finally {
              window.__arrangeClickRunning = false;
            }
          }, 0);
        }} />
          </Box>

          <RecentlyUploaded
            open={showRecent}
            onClose={() => setShowRecent(false)}
            onSelect={(imgData) => {
              setLocalImages(prev => {
                const added = {
                  ...imgData, // src, name, widthIn, heightIn, image, fromRecent
                  quantity: imgData.quantity ?? 1,
                  scale: imgData.scale ?? 100,
                  lockAspectRatio: imgData.lockAspectRatio ?? true,
                };
                const updated = [...prev, added];
                saveSessionImages(updated);
                return updated;
              });
            }}
          />
        </Box>
      </Box>
    </>
  );
}
